import {
  addMonths as addMonthsFns,
  differenceInDays,
  format,
  isValid,
  parse,
  subDays,
} from 'date-fns';
import { getIntlLocaleTag } from './locale';

/**
 * Checks whether date `b` falls on the calendar day immediately after date `a`
 * (i.e., whether `b` is the "next day" relative to `a`).
 *
 * Notes:
 * * This compares *calendar days* in the local timezone (year/month/day), not a
 *   duration of exactly 24 hours (so DST transitions are handled correctly).
 * * Inputs are not mutated.
 *
 * @param a - Base date.
 * @param b - Date to test.
 * @returns `true` if `b` is the next calendar day after `a`, otherwise `false`.
 */
export function isNextDay(a: Date, b: Date): boolean {
  const aNext = new Date(a.getTime());
  aNext.setDate(aNext.getDate() + 1);

  return (
    aNext.getFullYear() === b.getFullYear() &&
    aNext.getMonth() === b.getMonth() &&
    aNext.getDate() === b.getDate()
  );
}

/**
 * Checks whether two dates represent the same calendar day.
 *
 * @param a - First date.
 * @param b - Second date.
 * @returns `true` if `a` and `b` represent the same calendar day, otherwise `false`.
 */
export function isSameDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Formats a date as ISO date string, with optional time.
 *
 * Input:
 * * `date`: `Date` instance or a string.
 * * `time` (optional): Time string in `HH:mm` format.
 *
 * Output:
 * * Without `time`: `YYYY-MM-DD`
 * * With `time`: `YYYY-MM-DDTHH:mm`
 *
 * @throws Error if `date` or `time` has invalid format.
 */
export function formatIsoDate(date: Date | string, time?: string): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  if (!isValid(dateObj)) {
    throw new Error('Invalid date input.');
  }

  if (!time) {
    return format(dateObj, 'yyyy-MM-dd');
  }

  const timeObj = parse(time, 'HH:mm', new Date());
  if (!isValid(timeObj) || format(timeObj, 'HH:mm') !== time) {
    throw new Error(`Invalid time "${time}". Expected "HH:mm".`);
  }

  return `${format(dateObj, 'yyyy-MM-dd')}T${format(timeObj, 'HH:mm')}`;
}

export const DATE_FORMAT = 'dd.MM.yyyy';

export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
) {
  if (options) {
    return new Intl.DateTimeFormat(getIntlLocaleTag(), options).format(
      new Date(date),
    );
  }
  return format(new Date(date), DATE_FORMAT);
}

export function formatDateRange(
  from: Date | string,
  to: Date | string,
  options?: Intl.DateTimeFormatOptions,
) {
  const fromDate = from instanceof Date ? from : new Date(from);
  const toDate = to instanceof Date ? to : new Date(to);

  if (isSameDate(fromDate, toDate)) {
    return formatDate(fromDate, options);
  }

  if (options) {
    const fmt = new Intl.DateTimeFormat(getIntlLocaleTag(), options);
    if (typeof fmt.formatRange === 'function') {
      return fmt.formatRange(fromDate, toDate);
    }
    return `${fmt.format(fromDate)} – ${fmt.format(toDate)}`;
  }

  return `${format(fromDate, DATE_FORMAT)} – ${format(toDate, DATE_FORMAT)}`;
}

export function isoWeekday(date: string) {
  const w = new Date(date).getDay();
  return w === 0 ? 7 : w;
}

/**
 * Generate an inclusive range of Dates between start and end.
 *
 * @param startDate - The start date (inclusive).
 * @param endDate - The end date (inclusive).
 * @returns An array of Date objects for each calendar day from start to end, inclusive.
 */
export function daysRange(startDate: Date, endDate: Date): Date[] {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new Error(
      'Invalid date input: startDate or endDate is not a valid date',
    );
  }

  if (start > end) {
    return [];
  }

  const days: Date[] = [];
  const current = new Date(start);

  while (current <= end) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
}

/**
 * Accepts: JS Date or date-like string.
 * Returns: time string 'HH:mm'
 *
 * Parsing rules:
 * - Date: used directly
 * - String/number: parsed via new Date(value)
 */
export function toTime(date: Date | string | number): string {
  const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
  if (Number.isNaN(d.getTime())) {
    throw new Error('Invalid date input');
  }
  const pad2 = (n: number) => String(n).padStart(2, '0');
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function addMonths(date: Date, month: number): Date {
  return addMonthsFns(date, month);
}

export function subtractDay(date: Date, days: number): Date {
  return subDays(date, days);
}

export function datesDiff(dateFrom: Date, dateTo: Date) {
  return differenceInDays(dateTo, dateFrom) + 1;
}

export function hoursBetweenTime(start: string, end: string) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em - (sh * 60 + sm)) / 60;
}
